import type { Maybe } from '@adornis/base/utilTypes.js';
import { BillingRhythm } from '../../billingPositions/entities/BillingPosition.js';

/**
 * The lock a "Exportieren und sperren" run leaves on its group. Two dates, nothing else: the
 * Stichtag of the run and the day the group may be exported again. Locked is derived from them
 * and today's calendar day, so nothing has to run to release a group.
 */
export interface BillingLockState {
  billingLockStichtag?: Maybe<Date>;
  billingLockReleaseDay?: Maybe<Date>;
}

/**
 * Whether the group is locked today. A lock without a release day holds until an admin releases
 * it: what a once-only group's export leaves, and what a running export holds while it reads.
 */
export function isBillingLocked(group: BillingLockState, today: Date): boolean {
  if (!group.billingLockStichtag) return false;

  return !group.billingLockReleaseDay || today < group.billingLockReleaseDay;
}

/**
 * Whether a run for `stichtag` would bill a period the last lock export already billed. Read by
 * the export guard, which refuses such a run so no month is collected twice.
 */
export function isStichtagBilled(group: BillingLockState, stichtag: Date): boolean {
  if (!group.billingLockStichtag) return false;

  return !group.billingLockReleaseDay || stichtag < group.billingLockReleaseDay;
}

/**
 * The day the lock releases: the first of the next month. Every recurring group is exported monthly and each run
 * collects whoever is due on its own (`isDueOn`), so the release is never measured against the bookings. A once-only
 * group is billed for good: no release day, it stays locked until an admin releases it - a late booking too.
 */
export function getBillingLockReleaseDay(stichtag: Date, rhythm: Maybe<string>): Date | null {
  if (rhythm === BillingRhythm.ONCE_CUSTOM) return null;
  return new Date(Date.UTC(stichtag.getUTCFullYear(), stichtag.getUTCMonth() + 1, 1));
}

/**
 * Whether the lock also freezes the group's members and bookings, so nothing changes while its export is in flight.
 * Every locked group but a school: a school's bookings are its pupils' class memberships, and its Elternengagement
 * lock would stop the import, class changes and leavers for weeks; its mandates are held by their own lock. Nobody
 * edits a frozen group, admins included - "Freigeben" first. Bookings can still end, but not be deleted.
 */
export function freezesMembers(group: BillingLockState & { _class?: Maybe<string> }, today: Date): boolean {
  return group._class !== 'School' && isBillingLocked(group, today);
}
